import {
  createApi,
  defaultSerializeQueryArgs,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

import {
  cancelTraining,
  createRetraining,
  createTraining,
  deleteClause,
  deleteSample,
  deleteTrainingModel,
  getClientClause,
  getFiltersData,
  getFolderFilterOptions,
  getPredictionStatus,
  getSamples,
  getTrainingStatus,
  publishTrainedModel,
  renameClause,
  resumeClause,
  resumeSample,
  suspendClause,
  suspendSample,
  updateDescription,
  updatePredictionFilters,
  updateSample,
  updateTrainingLabels,
} from '~/api/automation';
import {
  getClient,
  getClientBySubdomain,
  getClientDepartmentsTree,
  getDataFieldSections,
} from '~/api/client';
import { deleteCrmIntegration, getCrmConnections } from '~/api/crms';
import { getFieldValues } from '~/api/dataFields';
import { getDocumentMetadataImportJobsData } from '~/api/documentMetadata';
import { Tags as EvisearchTags } from '~/api/evisearch/tags.autogen';
import {
  authenticateAriba,
  authenticateMultiTokensAriba,
  createS3TokenId,
  createSyncPair,
  getAribaFields,
  getAribaLocations,
  getAribaWorkspaceCount,
  getExternalId,
  getInternalFolders,
  getSyncPairs,
  getSyncPairsAriba,
  getSyncPairsAribaFailures,
  processFieldMappingCsv,
  updateAribaFileSyncPair,
  updateSyncPair,
  upddateTokensSyncPair,
  validateAribaDocumentIdFile,
  validateFieldMappingCsv,
  validateRealm,
  validateSyncPairFolders,
} from '~/api/evisync';
import { getEsignatureIntegrations as getESignatureIntegrationsV1 } from '~/api/integrations';
import { checkEnvelope, reassignApproval } from '~/api/tickets';
import { getAllUsers, getUserImportJobsData } from '~/api/users';
import {
  getAvailableWorkflows,
  getWorkflowFile,
  saveAndPublishWorkflow,
} from '~/api/workflows';

import { evisearch } from './evisearch';
import { bulkUpdateAccess } from './methods/access-control/bulkUpdateAccess';
import { getBulkAccessPreview } from './methods/access-control/getBulkAccessPreview';
import { getResourceAccess } from './methods/access-control/getResourceAccess';
import { updateResourceAccess } from './methods/access-control/updateResourceAccess';
import * as acl from './methods/acl';
import { createAgentExecution } from './methods/agent-execution/createAgentExecution';
import { getAgentExecution } from './methods/agent-execution/getAgentExecution';
import { getAgentExecutions } from './methods/agent-execution/getAgentExecutions';
import { interruptAgentExecution } from './methods/agent-execution/interruptAgentExecution';
import { patchAgentExecutionMessage } from './methods/agent-execution/patchMessage';
import { sendAgentExecutionMessage } from './methods/agent-execution/sendMessage';
import {
  INITIAL_STREAM_STATE,
  onStreamCacheEntryAdded,
  type StreamAgentExecutionArgs,
} from './methods/agent-execution/streamAgentExecution';
import { getAgentForgeInvocations } from './methods/agentforge/getAgentForgeInvocations';
import { getFolderTree } from './methods/alert';
import { createAlert } from './methods/alerts/createAlert';
import { deleteAlerts } from './methods/alerts/deleteAlerts';
import { getAlert } from './methods/alerts/getAlert';
import { getAlerts } from './methods/alerts/getAlerts';
import { sendAlertEmail } from './methods/alerts/sendAlertEmail';
import { toggleMuteAlert } from './methods/alerts/toggleMuteAlert';
import { updateAlert } from './methods/alerts/updateAlert';
import { getAlgorithmMetrics } from './methods/algorithms/getAlgorithmMetrics';
import { reprocessAlgorithms } from './methods/algorithms/reprocessAlgorithms';
import { stopReprocessAlgorithms } from './methods/algorithms/stopReprocessAlgorithms';
import {
  createApiKey,
  deleteApiKey,
  getApiKeys,
  updateApiKey,
} from './methods/apiKeys';
import { resolveCitation } from './methods/ask-anything/resolveCitation';
import { getAuditLogs } from './methods/auditlogs/getAuditLogs';
import { getSsoProvider } from './methods/auth';
import { createSample } from './methods/automation/createSample';
import { getClauses } from './methods/automation/getClauses';
import { getIdentifications } from './methods/automation/getIdentifications';
import { updateClause } from './methods/automation/updateClause';
import {
  clauseLibraryCreateClause,
  clauseLibraryCreateVariation,
  clauseLibraryDeleteClause,
  clauseLibraryDeleteVariation,
  clauseLibraryEditClause,
  clauseLibraryEditVariation,
  clauseLibraryGetClause,
  clauseLibraryListClauses,
  clauseLibrarySearchClauses,
} from './methods/clauseLibrary';
import { copyWorkflow } from './methods/client/copyWorkflow';
import { createClient } from './methods/client/createClient';
import { deleteClient } from './methods/client/deleteClient';
import { enableWellKnownFields } from './methods/client/enableWellKnownFields';
import { getBucketInfo } from './methods/client/getBucketInfo';
import { getClientContractTypes } from './methods/client/getClientContractTypes';
import { getClientFeatureFlags } from './methods/client/getClientFeatureFlags';
import { getClients } from './methods/client/getClients';
import { getCustomAlgorithmChoices } from './methods/client/getCustomAlgorithmChoices';
import { getEvisearchContractTypes } from './methods/client/getEvisearchContractTypes';
import { getExistingClientBySubdomain } from './methods/client/getExistingClientBySubdomain';
import { getIsWorkdayClient } from './methods/client/getIsWorkdayClient';
import { getWellKnownFieldsStatus } from './methods/client/getWellKnownFieldsStatus';
import { updateClient } from './methods/client/updateClient';
import { updateClientAdmin } from './methods/client/updateClientAdmin';
import { createOneTimeCreditBalance } from './methods/codecash/createOneTimeCreditBalance';
import { editOneTimeCreditBalance } from './methods/codecash/editOneTimeCreditBalance';
import { getCreditEstimation } from './methods/codecash/getCreditEstimation';
import { getCreditLedger } from './methods/codecash/getCreditLedger';
import { getCreditSummary } from './methods/codecash/getCreditSummary';
import { updateRecurringCredits } from './methods/codecash/updateRecurringCredits';
import {
  acceptLegalDisclaimer,
  interruptAiMessage,
} from './methods/conversationalAi';
import { getCurrencies } from './methods/currencies/getCurrencies';
import { getDashboard, getDashboards } from './methods/dashboards';
import {
  createDefaultDashboards,
  deleteDashboard,
  saveAsDashboard,
  saveDashboardChanges,
  updateDashboard,
} from './methods/dashboardV2';
import { createClientSectionData } from './methods/data-fields/createClientSectionData';
import { createDataField } from './methods/data-fields/createDataField';
import { deleteClientSection } from './methods/data-fields/deleteClientSection';
import { deleteDataField } from './methods/data-fields/deleteDataField';
import { getClientDataFieldSections } from './methods/data-fields/getClientDataFieldSections';
import { moveDataFieldToSection } from './methods/data-fields/moveDataFieldToSection';
import { reorderClientSectionsData } from './methods/data-fields/reorderClientSectionsData';
import { reorderDataFields } from './methods/data-fields/reorderDataFields';
import { updateClientSectionName } from './methods/data-fields/updateClientSectionName';
import { updateDataField } from './methods/data-fields/updateDataField';
import { createDdeModel } from './methods/dde/createDdeModel';
import { createDdeModelVersion } from './methods/dde/createDdeModelVersion';
import { createDdeTable } from './methods/dde/createDdeTable';
import { deleteDdeModel } from './methods/dde/deleteDdeModel';
import { deleteDdeTable } from './methods/dde/deleteDdeTable';
import { deleteDdeTableDocument } from './methods/dde/deleteDdeTableDocument';
import { extractDdeTable } from './methods/dde/extractDdeTable';
import { getDdeTable } from './methods/dde/getDdeTable';
import { getDdeTableRows } from './methods/dde/getDdeTableRows';
import { getDdeTables } from './methods/dde/getDdeTables';
import { listDdeModels } from './methods/dde/listDdeModels';
import { updateDdeTable } from './methods/dde/updateDdeTable';
import { uploadDdeTableDocuments } from './methods/dde/uploadDdeTableDocuments';
import {
  getDeletedDocuments,
  getDeletedDocumentsDeletedByUsers,
  getDeletedDocumentsForCurrentUser,
  getDeletedDocumentsOriginalFolder,
  getDeletedDocumentsOriginalFolderForCurrentUser,
  permanentlyDeleteDocuments,
  permanentlyDeleteDocumentsForCurrentUser,
  restoreDeletedDocuments,
  restoreDeletedDocumentsForCurrentUser,
} from './methods/deletedDocument';
import {
  findDepartmentsByName,
  getAllDepartments,
  getDepartmentChildren,
  listDepartmentMembers,
} from './methods/departments';
import {
  backupDocumentSfdt,
  compareDocumentsSfdt,
  convertContentToSfdt,
  convertPdfToDocx,
  deleteDocumentClause,
  downloadOcrPdf,
  getAllClauses,
  getDocumentAlgoStatus,
  getDocumentByTag,
  getDocumentContent,
  getDocumentDetailClauses,
  getDocumentFromVersion,
  getDocumentOriginal,
  getDocumentSfdt,
  getDocumentVersionFileByTag,
  getDocumentVersionOcrHtml,
  getMigratedClauses,
  resolveDocument,
  saveDocumentSfdt,
  tokenizeRiskContent,
  tokenizeSfdtContent,
  updateClauseContent,
} from './methods/document';
import { documentBulkAddReplaceFields } from './methods/document-bulk-actions/bulkAddReplaceFields';
import { searchV2BulkCopy } from './methods/document-bulk-actions/bulkCopy';
import { searchV2BulkDelete } from './methods/document-bulk-actions/bulkDelete';
import { searchV2BulkEditClause } from './methods/document-bulk-actions/bulkEditClauses';
import { documentBulkEditFields } from './methods/document-bulk-actions/bulkEditFields';
import { searchV2BulkMove } from './methods/document-bulk-actions/bulkMove';
import { searchV2BulkEditData } from './methods/document-bulk-actions/getBulkEditFieldsOptions';
import { getDocument } from './methods/document-folder/getDocument';
import { deleteDocumentHandler } from './methods/document/deleteDocumentHandler';
import { downloadAttachment } from './methods/document/downloadAttachment';
import { downloadDocumentAttachment } from './methods/document/downloadDocumentAttachment';
import { getCLMeDocumentClauses } from './methods/document/getCLMeDocumentClauses';
import { getCLMeDocumentContent } from './methods/document/getCLMeDocumentContent';
import { getCLMeDocumentInformation } from './methods/document/getCLMeDocumentInformation';
import { getDocumentFields } from './methods/document/getDocumentFields';
import { getDocumentHandler } from './methods/document/getDocumentHandler';
import { putDocumentUpdate } from './methods/document/putDocumentUpdate';
import { renameDocumentHandler } from './methods/document/renameDocumentHandler';
import { uploadAttachments } from './methods/document/uploadAttachments';
import {
  addDocumentsToGroup,
  addDocumentsToGroupByFolder,
  addDocumentsToGroupLinkParent,
  createDocumentGroup,
  deleteDocumentGroup,
  editDocumentGroup,
  getAllDocumentGroups,
  getDocumentGroup,
  linkDocumentsToGroup,
  removeDocumentInGroup,
  unlinkDocumentsFromGroup,
} from './methods/documentGroups';
import { getFileLevelEvents } from './methods/evi-sync/getFileLevelEvents';
import { testWorkdayConnectivity } from './methods/evi2ken/testWorkdayConnectivity';
import { getExternalDocument } from './methods/external/getExternalDocument';
import { getExternalDocumentForDebug } from './methods/external/getExternalDocumentForDebug';
import { createField } from './methods/fields/createField';
import { getDocumentInformationFieldByUfField } from './methods/fields/getDocumentInformationFieldByUfField';
import { getFields } from './methods/fields/getFields';
import { getFieldSections } from './methods/fields/getFieldSections';
import { getFieldUfId } from './methods/fields/getFieldUfId';
import { createFolder } from './methods/folder/createFolder';
import { folderBulkCopy } from './methods/folder/folderBulkCopy';
import { folderBulkDelete } from './methods/folder/folderBulkDelete';
import { folderBulkMove } from './methods/folder/folderBulkMove';
import { getFolderAccessList } from './methods/folder/getFolderAccessList';
import { getFolderVisibility } from './methods/folder/getFolderVisibility';
import { renameFolder } from './methods/folder/renameFolder';
import { updateFolderAccessSettings } from './methods/folder/updateFolderAccessSettings';
import { getGenClauseResult, getGenClauseResults } from './methods/genAi';
import { getHighlights, postHighlight } from './methods/highlights';
import { applyHomeScope } from './methods/home/applyHomeScope';
import { clearHomeScope } from './methods/home/clearHomeScope';
import { addParty } from './methods/parties/addParty';
import { deleteParties } from './methods/parties/deleteParties';
import { deleteParty } from './methods/parties/deleteParty';
import { getParties } from './methods/parties/getParties';
import { updateParty } from './methods/parties/updateParty';
import { uploadParty } from './methods/parties/uploadParty';
import { getDocumentHandlerPaths } from './methods/path/getDocumentHandlerPaths';
import {
  fetchGroupForDocument,
  getDocumentsViewsetFields,
  getFolderData,
} from './methods/postSigDocument';
import { getClauseSuggestions } from './methods/provisions/getClauseSuggestions';
import { checkAllPermissions } from './methods/roles/checkAllPermissions';
import {
  cancelUserInvite,
  createRole,
  createUser,
  deactivateUser,
  deleteRole,
  reactivateUser,
  resendReactivatedUserInvite,
  sendInvitationEmail,
  updateRole,
  updateUser,
  validateRoleName,
} from './methods/roles/roles';
import { createRouting } from './methods/routing/createRouting';
import { deleteRouting } from './methods/routing/deleteRouting';
import { getRoutingById } from './methods/routing/getRoutingById';
import { getRoutingConfiguration } from './methods/routing/getRoutingConfiguration';
import { getUnroutedWorkflows } from './methods/routing/getUnroutedWorkflows';
import { patchRoutingConfiguration } from './methods/routing/patchRoutingConfiguration';
import { searchRouting } from './methods/routing/searchRouting';
import { updateRouting } from './methods/routing/updateRouting';
import {
  checkDownloadExportReportById,
  deleteSavedSearch,
  exportSearchResults,
  getFolders,
  getQuickSearchRecords,
  getSavedSearches,
  getSearchHandlersByPosition,
  getSearchPositionByHandler,
  saveSearch,
  searchDocuments,
  updatePinnedField,
  updateSavedSearch,
  validateBooleanQuery,
} from './methods/search';
import {
  exportExcelV2,
  getChartData,
  getFilters,
  getFilterSuggestions,
  searchDocuments as searchV3Documents,
} from './methods/searchV3';
import { deleteSearchV3SavedSearch } from './methods/searchV3/deleteSavedSearch';
import { getSavedSearch } from './methods/searchV3/getSavedSearch';
import { getSavedSearches as getSearchV3SavedSearches } from './methods/searchV3/getSavedSearches';
import { postSavedSearch } from './methods/searchV3/postSavedSearch';
import { putSavedSearch } from './methods/searchV3/putSavedSearch';
import { updateCurrentUser } from './methods/settings/updateCurrentUser';
import { updatePassword } from './methods/settings/updatePassword';
import { createSharepointConfiguration } from './methods/sharepoint-embedded/createSharepointConfiguration';
import { deleteSharepointConfiguration } from './methods/sharepoint-embedded/deleteSharepointConfiguration';
import { getGraphApiPermissions } from './methods/sharepoint-embedded/getGraphApiPermissions';
import { getSharepointConfigurationStatus } from './methods/sharepoint-embedded/getSharepointConfigurationStatus';
import {
  getEditSessionStatus,
  getSharepointEmbeddedUrl,
  unlockSharepointDocument,
  type UnlockSharepointDocumentParams,
} from './methods/spe';
import { createSuperAdmin, deactivateSuperAdmin } from './methods/superAdmin';
import {
  createTableView,
  deleteTableView,
  getCurrentTableView,
  getDefaultTableView,
  getTableViews,
  loadDefaultTableView,
  loadTableView,
  renameTableView,
  updateTableView,
} from './methods/tableViews';
import {
  addTicketParticipants,
  bumpVersion,
  cancelTicket,
  collectSignatures,
  completeTicket,
  createActivityLog,
  deleteRecipients,
  getActivities,
  getTicketCurrentStage,
  getTicketDocument,
  getTicketIntakeForm,
  getTicketParticipants,
  getTicketSummary,
  updateEnvelope,
  updateRecipients,
} from './methods/ticket';
import {
  downloadTicketDocumentVersionEmailAttachment,
  getTicketDocumentVersionEditorKey,
} from './methods/ticketDocumentVersion';
import { assignSigner } from './methods/tickets/esignature/assignSigner';
import { createEnvelope } from './methods/tickets/esignature/createEnvelope';
import { createMultiTicketBundle } from './methods/tickets/esignature/createMultiTicketBundle';
import { getEnvelope } from './methods/tickets/esignature/getEnvelope';
import { getEsignatureActivities } from './methods/tickets/esignature/getEsignatureActivities';
import { getRecipients } from './methods/tickets/esignature/getRecipients';
import { sendReminder } from './methods/tickets/esignature/sendReminder';
import { signDocument } from './methods/tickets/esignature/signDocument';
import { validateMultiTicketBundle } from './methods/tickets/esignature/validateMultiTicketBundle';
import { viewEnvelope } from './methods/tickets/esignature/viewEnvelope';
import { getTicketWellKnownFields } from './methods/tickets/getTicketWellKnownFields';
import { postTicketNextStage } from './methods/tickets/postTicketNextStage';
import { putTicket } from './methods/tickets/putTicket';
import {
  createTurnTrackingEntry,
  getTurnTrackingHistory,
  getTurnTrackingParties,
  updateTurnTrackingEntry,
} from './methods/turnTracking';
import { addUploadFiles } from './methods/upload/addUploadFiles';
import { getUploadFiles } from './methods/upload/getUploadFiles';
import {
  getHomeSettings,
  getLocalizationPreferences,
  getPinnedFilters,
  getRecentSearches,
  updateHomeSettings,
  updateLocalizationPreferences,
  updatePinnedFilters,
  updateRecentSearches,
} from './methods/userPreferences';
import {
  acknowledgeUserImport,
  cancelImportJob,
  downloadImportResultsExcelFile,
  exportUsersExcelFile,
  findUsersByNameOrEmail,
  getUsersImportStatus,
  importUsersListExcelFile,
  resolveUser,
  resolveUsers,
  startImportUsersListProcessing,
} from './methods/users';
import { activateEndpoint } from './methods/webhooks/activateEndpoint';
import { archiveAttempt } from './methods/webhooks/archiveAttempt';
import { createEndpoint } from './methods/webhooks/createEndpoint';
import { deleteEndpoint } from './methods/webhooks/deleteEndpoint';
import { getAttempts } from './methods/webhooks/getAttempts';
import { getAttemptsExport } from './methods/webhooks/getAttemptsExport';
import { getConfigurationEvents } from './methods/webhooks/getConfigurationEvents';
import { getEndpoints } from './methods/webhooks/getEndpoints';
import { getEventFields } from './methods/webhooks/getEventFields';
import { getEventSamplePayload } from './methods/webhooks/getEventSamplePayload';
import { resendAttempt } from './methods/webhooks/resendAttempt';
import { rotateEndpointKey } from './methods/webhooks/rotateEndpointKey';
import { suspendEndpoint } from './methods/webhooks/suspendEndpoint';
import { updateEndpoint } from './methods/webhooks/updateEndpoint';
import { getWopiSetup } from './methods/wopi/getWopiSetup';
import { getWopiStaticFiles } from './methods/wopi/getWopiStaticFiles';
import { getWorkdayUpcApps } from './methods/workday/getWorkdayUpcApps';
import { createWorkflow } from './methods/workflows/createWorkflow';
import { deleteWorkflow } from './methods/workflows/deleteWorkflow';
import { duplicateWorkflow } from './methods/workflows/duplicateWorkflow';
import { getEverestAmendability } from './methods/workflows/getEverestAmendability';
import { getIntegrateWorkflowCompanies } from './methods/workflows/getIntegrateWorkflowCompanies';
import { getIntegrateWorkflowIntakeForm } from './methods/workflows/getIntegrateWorkflowIntakeForm';
import { getIntegrateWorkflowSuppliers } from './methods/workflows/getIntegrateWorkflowSuppliers';
import { getWorkflow } from './methods/workflows/getWorkflow';
import { getWorkflowContractTypes } from './methods/workflows/getWorkflowContractTypes';
import { getWorkflows } from './methods/workflows/getWorkflows';
import { getWorkflowShareSettings } from './methods/workflows/getWorkflowShareSettings';
import { publishWorkflow } from './methods/workflows/publishWorkflow';
import { unpublishWorkflow } from './methods/workflows/unpublishWorkflow';
import { updateWorkflowShareSettings } from './methods/workflows/updateWorkflowShareSettings';
import { getSupplierCompany } from './methods/wss/getSupplierCompany';
import { postTicketSupplierNegotiation } from './methods/wss/postTicketSupplierNegotiation';
import { resolveWssDocument } from './methods/wss/resolveWssDocument';
import { resolveWssTicketId } from './methods/wss/resolveWssTicket';
import { extractRtkQueryRehydrationInfo } from './rehydrate';
import { TagType } from './TagType';
import {
  createConversation,
  deleteConversation,
  getConversation,
  getConversationHistory,
  getConversations,
  patchConversation,
  postUserFeedback,
  postUserMessage,
} from './transformers/conversationalAi';
import { getDashboardsV2, getDashboardV2 } from './transformers/dashboardV2';
import { getAllClausesGrouped } from './transformers/documents';
import {
  getPermissions,
  getRoles,
  getRolesSimple,
  getUsers,
} from './transformers/roles';
import { getFilterSections } from './transformers/search';
import { getSearchExportOptions } from './transformers/searchV3';
import {
  createQueryFn,
  extractQueryOptions,
  withOptions,
  type PayloadWithQueryOptions,
} from './utils';

const FIVE_MINUTES = 5 * 60;
const ONE_HOUR = 60 * 60;
export const api = createApi({
  reducerPath: 'API Service',
  baseQuery: fetchBaseQuery(),
  keepUnusedDataFor: FIVE_MINUTES,
  refetchOnFocus: false,
  tagTypes: Object.values(TagType),
  extractRehydrationInfo: extractRtkQueryRehydrationInfo,
  serializeQueryArgs: (args) => {
    // Strip rtkQueryOptions from cache key so it doesn't affect caching
    const { payload } = extractQueryOptions(args.queryArgs);
    return defaultSerializeQueryArgs({ ...args, queryArgs: payload });
  },
  endpoints: (build) => ({
    // auth
    getSsoProvider: build.query({
      queryFn: createQueryFn(getSsoProvider),
    }),

    // users
    getAllUsers: build.query({
      queryFn: createQueryFn(getAllUsers),
    }),
    getFieldValues: build.query({
      queryFn: createQueryFn(getFieldValues),
    }),
    getUserImportJob: build.query({
      queryFn: createQueryFn(getUserImportJobsData),
    }),
    getUsersImportStatus: build.query({
      queryFn: createQueryFn(getUsersImportStatus),
    }),
    acknowledgeUserImport: build.mutation({
      queryFn: createQueryFn(acknowledgeUserImport),
    }),
    getDataFieldSections: build.query({
      queryFn: createQueryFn(getDataFieldSections),
    }),
    findUsersByNameOrEmail: build.query({
      queryFn: createQueryFn(findUsersByNameOrEmail),
    }),
    exportUsersExcelFile: build.query({
      queryFn: createQueryFn(exportUsersExcelFile),
    }),
    downloadImportResultsExcelFile: build.query({
      queryFn: createQueryFn(downloadImportResultsExcelFile),
    }),
    startImportUsersListProcessing: build.mutation({
      queryFn: createQueryFn(startImportUsersListProcessing),
    }),
    importUsersListExcelFile: build.mutation({
      queryFn: createQueryFn(importUsersListExcelFile),
    }),
    cancelImportJob: build.mutation({
      queryFn: createQueryFn(cancelImportJob),
    }),
    testWorkdayConnectivity: build.query({
      queryFn: createQueryFn(testWorkdayConnectivity),
    }),

    //clients
    updateClientAdmin: build.mutation({
      queryFn: createQueryFn(updateClientAdmin),
    }),
    getClients: build.query({
      queryFn: createQueryFn(getClients),
      keepUnusedDataFor: 600,
      providesTags: () => [
        {
          type: TagType.Clients,
          id: 'LIST',
        },
      ],
    }),
    deleteClient: build.mutation({
      queryFn: createQueryFn(deleteClient),
      invalidatesTags: () => [
        {
          type: TagType.Clients,
          id: 'LIST',
        },
      ],
    }),
    updateClient: build.mutation({
      queryFn: createQueryFn(updateClient),
      invalidatesTags: () => [
        {
          type: TagType.Clients,
          id: 'LIST',
        },
      ],
    }),
    createClient: build.mutation({
      queryFn: createQueryFn(createClient),
      invalidatesTags: () => [
        {
          type: TagType.Clients,
          id: 'LIST',
        },
      ],
    }),

    getBucketInfo: build.query({
      queryFn: createQueryFn(getBucketInfo),
    }),

    getCustomAlgorithmChoices: build.query({
      queryFn: createQueryFn(getCustomAlgorithmChoices),
    }),

    copyWorkflow: build.mutation({
      queryFn: createQueryFn(copyWorkflow),
    }),
    getClientFeatureFlags: build.query({
      queryFn: createQueryFn(getClientFeatureFlags),
    }),

    getExistingClientBySubdomain: build.query({
      queryFn: createQueryFn(getExistingClientBySubdomain),
    }),

    // Clause Library
    clauseLibraryCreateClause: build.mutation({
      queryFn: createQueryFn(clauseLibraryCreateClause),
    }),
    clauseLibraryListClauses: build.query({
      queryFn: createQueryFn(clauseLibraryListClauses),
    }),
    clauseLibrarySearchClauses: build.query({
      queryFn: createQueryFn(clauseLibrarySearchClauses),
    }),
    clauseLibraryGetClause: build.query({
      queryFn: createQueryFn(clauseLibraryGetClause),
    }),
    clauseLibraryEditClause: build.mutation({
      queryFn: createQueryFn(clauseLibraryEditClause),
    }),
    clauseLibraryDeleteClause: build.mutation({
      queryFn: createQueryFn(clauseLibraryDeleteClause),
    }),
    clauseLibraryCreateVariation: build.mutation({
      queryFn: createQueryFn(clauseLibraryCreateVariation),
    }),
    clauseLibraryEditVariation: build.mutation({
      queryFn: createQueryFn(clauseLibraryEditVariation),
    }),
    clauseLibraryDeleteVariation: build.mutation({
      queryFn: createQueryFn(clauseLibraryDeleteVariation),
    }),

    // Automation Hub
    getClientClause: build.query({
      queryFn: createQueryFn(getClientClause),
    }),
    getClauses: build.query({
      queryFn: createQueryFn(getClauses),
    }),
    suspendClause: build.mutation({
      queryFn: createQueryFn(suspendClause),
    }),
    resumeClause: build.mutation({
      queryFn: createQueryFn(resumeClause),
    }),
    deleteClause: build.mutation({
      queryFn: createQueryFn(deleteClause),
    }),
    cancelTraining: build.mutation({
      queryFn: createQueryFn(cancelTraining),
    }),
    renameClause: build.mutation({
      queryFn: createQueryFn(renameClause),
    }),
    updateClause: build.mutation({
      queryFn: createQueryFn(updateClause),
    }),
    getIdentifications: build.query({
      queryFn: createQueryFn(getIdentifications),
    }),
    getSamples: build.query({
      queryFn: createQueryFn(getSamples),
    }),
    createSample: build.mutation({
      queryFn: createQueryFn(createSample),
    }),
    suspendSample: build.mutation({
      queryFn: createQueryFn(suspendSample),
    }),
    resumeSample: build.mutation({
      queryFn: createQueryFn(resumeSample),
    }),
    updateSample: build.mutation({
      queryFn: createQueryFn(updateSample),
    }),
    deleteSample: build.mutation({
      queryFn: createQueryFn(deleteSample),
    }),

    // SmartClause
    createTraining: build.mutation({
      queryFn: createQueryFn(createTraining),
    }),
    updateTrainingLabels: build.mutation({
      queryFn: createQueryFn(updateTrainingLabels),
    }),
    createRetraining: build.mutation({
      queryFn: createQueryFn(createRetraining),
    }),
    getTrainingStatus: build.query({
      queryFn: createQueryFn(getTrainingStatus),
    }),
    getFiltersData: build.query({
      queryFn: createQueryFn(getFiltersData),
    }),
    publishTrainedModel: build.mutation({
      queryFn: createQueryFn(publishTrainedModel),
    }),
    getPredictionStatus: build.query({
      queryFn: createQueryFn(getPredictionStatus),
    }),
    updatePredictionFilters: build.mutation({
      queryFn: createQueryFn(updatePredictionFilters),
    }),
    deleteTrainingModel: build.mutation({
      queryFn: createQueryFn(deleteTrainingModel),
    }),
    updateDescription: build.mutation({
      queryFn: createQueryFn(updateDescription),
    }),
    getFolderFilterOptions: build.query({
      queryFn: createQueryFn(getFolderFilterOptions),
    }),

    // Workflow
    getAvailableWorkflows: build.query({
      queryFn: createQueryFn(getAvailableWorkflows),
    }),
    getWorkflow: build.query({
      queryFn: createQueryFn(getWorkflow),
    }),
    getWorkflows: build.query({
      queryFn: createQueryFn(getWorkflows),
    }),
    getEverestAmendability: build.query({
      queryFn: createQueryFn(getEverestAmendability),
    }),
    getIntegrateWorkflowIntakeForm: build.query({
      queryFn: createQueryFn(getIntegrateWorkflowIntakeForm),
    }),
    getIntegrateWorkflowSuppliers: build.query({
      queryFn: createQueryFn(getIntegrateWorkflowSuppliers),
    }),
    getIntegrateWorkflowCompanies: build.query({
      queryFn: createQueryFn(getIntegrateWorkflowCompanies),
    }),
    publishWorkflow: build.mutation({
      queryFn: createQueryFn(publishWorkflow),
    }),
    saveAndPublishWorkflow: build.mutation({
      queryFn: createQueryFn(saveAndPublishWorkflow),
    }),
    createWorkflow: build.mutation({
      queryFn: createQueryFn(createWorkflow),
    }),
    duplicateWorkflow: build.mutation({
      queryFn: createQueryFn(duplicateWorkflow),
    }),
    unpublishWorkflow: build.mutation({
      queryFn: createQueryFn(unpublishWorkflow),
    }),
    deleteWorkflow: build.mutation({
      queryFn: createQueryFn(deleteWorkflow),
    }),
    getWorkflowShareSettings: build.query({
      queryFn: createQueryFn(getWorkflowShareSettings),
    }),
    updateWorkflowShareSettings: build.mutation({
      queryFn: createQueryFn(updateWorkflowShareSettings),
    }),
    // ApiKey
    getApiKeys: build.query({
      queryFn: createQueryFn(getApiKeys),
    }),
    createApiKey: build.mutation({
      queryFn: createQueryFn(createApiKey),
    }),
    updateApiKey: build.mutation({
      queryFn: createQueryFn(updateApiKey),
    }),
    deleteApiKey: build.mutation({
      queryFn: createQueryFn(deleteApiKey),
    }),

    // Client
    getClient: build.query({
      queryFn: createQueryFn(getClient),
    }),
    getClientBySubdomain: build.query({
      queryFn: createQueryFn(getClientBySubdomain),
    }),
    getClientContractTypes: build.query({
      queryFn: createQueryFn(getClientContractTypes),
    }),
    getEvisearchContractTypes: build.query({
      queryFn: createQueryFn(getEvisearchContractTypes),
    }),
    getIsWorkdayClient: build.query({
      queryFn: createQueryFn(getIsWorkdayClient),
    }),
    getWellKnownFieldsStatus: build.query({
      queryFn: createQueryFn(getWellKnownFieldsStatus),
    }),
    enableWellKnownFields: build.mutation({
      queryFn: createQueryFn(enableWellKnownFields),
    }),

    // Dashboard
    getDashboards: build.query({
      queryFn: createQueryFn(getDashboards),
    }),
    getDashboard: build.query({
      queryFn: createQueryFn(getDashboard),
    }),

    // Data Fields
    getClientDataFieldSections: build.query({
      queryFn: createQueryFn(getClientDataFieldSections),
      providesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),

    createClientSectionData: build.mutation({
      queryFn: createQueryFn(createClientSectionData),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),
    reorderClientSectionsData: build.mutation({
      queryFn: createQueryFn(reorderClientSectionsData),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),

    updateClientSectionName: build.mutation({
      queryFn: createQueryFn(updateClientSectionName),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),

    deleteDataField: build.mutation({
      queryFn: createQueryFn(deleteDataField),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),

    deleteClientSection: build.mutation({
      queryFn: createQueryFn(deleteClientSection),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),

    createDataField: build.mutation({
      queryFn: createQueryFn(createDataField),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),
    createDdeTable: build.mutation({
      queryFn: createQueryFn(createDdeTable),
      invalidatesTags: () => [{ type: TagType.DdeTables, id: 'LIST' }],
    }),
    createDdeModel: build.mutation({
      queryFn: createQueryFn(createDdeModel),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    createDdeModelVersion: build.mutation({
      queryFn: createQueryFn(createDdeModelVersion),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    getDdeTables: build.query({
      queryFn: createQueryFn(getDdeTables),
      providesTags: () => [{ type: TagType.DdeTables, id: 'LIST' }],
    }),
    getDdeTable: build.query({
      queryFn: createQueryFn(getDdeTable),
      providesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    getDdeTableRows: build.query({
      queryFn: createQueryFn(getDdeTableRows),
      providesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    listDdeModels: build.query({
      queryFn: createQueryFn(listDdeModels),
      providesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    updateDdeTable: build.mutation({
      queryFn: createQueryFn(updateDdeTable),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
        { type: TagType.DdeTables, id: 'LIST' },
      ],
    }),
    uploadDdeTableDocuments: build.mutation({
      queryFn: createQueryFn(uploadDdeTableDocuments),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    deleteDdeTableDocument: build.mutation({
      queryFn: createQueryFn(deleteDdeTableDocument),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    deleteDdeModel: build.mutation({
      queryFn: createQueryFn(deleteDdeModel),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    deleteDdeTable: build.mutation({
      queryFn: createQueryFn(deleteDdeTable),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
        { type: TagType.DdeTables, id: 'LIST' },
      ],
    }),
    extractDdeTable: build.mutation({
      queryFn: createQueryFn(extractDdeTable),
      invalidatesTags: (_result, _error, { tableId }) => [
        { type: TagType.DdeTables, id: tableId },
      ],
    }),
    updateDataField: build.mutation({
      queryFn: createQueryFn(updateDataField),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),

    reorderDataFields: build.mutation({
      queryFn: createQueryFn(reorderDataFields),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),

    moveFieldToSection: build.mutation({
      queryFn: createQueryFn(moveDataFieldToSection),
      invalidatesTags: () => [
        {
          type: TagType.DataFields,
          id: 'LIST',
        },
      ],
    }),
    // Departments
    getClientDepartmentsTree: build.query({
      queryFn: createQueryFn(getClientDepartmentsTree),
    }),
    getAllDepartments: build.query({
      queryFn: createQueryFn(getAllDepartments),
    }),
    findDepartmentsByName: build.query({
      queryFn: createQueryFn(findDepartmentsByName),
    }),
    listDepartmentMembers: build.query({
      queryFn: createQueryFn(listDepartmentMembers),
    }),

    // Document Groups
    getAllDocumentGroups: build.query({
      queryFn: createQueryFn(getAllDocumentGroups),
    }),
    getDocumentGroup: build.query({
      queryFn: createQueryFn(getDocumentGroup),
    }),
    addDocumentsToGroup: build.mutation({
      queryFn: createQueryFn(addDocumentsToGroup),
    }),
    addDocumentsToGroupByFolder: build.mutation({
      queryFn: createQueryFn(addDocumentsToGroupByFolder),
    }),
    addDocumentsToGroupLinkParent: build.mutation({
      queryFn: createQueryFn(addDocumentsToGroupLinkParent),
    }),
    linkDocumentsToGroup: build.mutation({
      queryFn: createQueryFn(linkDocumentsToGroup),
    }),
    unlinkDocumentsFromGroup: build.mutation({
      queryFn: createQueryFn(unlinkDocumentsFromGroup),
    }),
    createDocumentGroup: build.mutation({
      queryFn: createQueryFn(createDocumentGroup),
    }),
    removeDocumentInGroup: build.mutation({
      queryFn: createQueryFn(removeDocumentInGroup),
    }),
    editDocumentGroup: build.mutation({
      queryFn: createQueryFn(editDocumentGroup),
    }),
    deleteDocumentGroup: build.mutation({
      queryFn: createQueryFn(deleteDocumentGroup),
    }),

    // Document Restore
    getDeletedDocuments: build.query({
      queryFn: createQueryFn(getDeletedDocuments),
    }),
    getDeletedDocumentsForCurrentUser: build.query({
      queryFn: createQueryFn(getDeletedDocumentsForCurrentUser),
    }),
    permanentlyDeleteDocuments: build.mutation({
      queryFn: createQueryFn(permanentlyDeleteDocuments),
    }),
    permanentlyDeleteDocumentsForCurrentUser: build.mutation({
      queryFn: createQueryFn(permanentlyDeleteDocumentsForCurrentUser),
    }),
    restoreDeletedDocuments: build.mutation({
      queryFn: createQueryFn(restoreDeletedDocuments),
    }),
    restoreDeletedDocumentsForCurrentUser: build.mutation({
      queryFn: createQueryFn(restoreDeletedDocumentsForCurrentUser),
    }),
    getDeletedDocumentsOriginalFolder: build.query({
      queryFn: createQueryFn(getDeletedDocumentsOriginalFolder),
    }),
    getDeletedDocumentsOriginalFolderForCurrentUser: build.query({
      queryFn: createQueryFn(getDeletedDocumentsOriginalFolderForCurrentUser),
    }),
    getDeletedDocumentsDeletedByUsers: build.query({
      queryFn: createQueryFn(getDeletedDocumentsDeletedByUsers),
    }),

    // Evisync
    getExternalId: build.query({
      queryFn: createQueryFn(getExternalId),
    }),
    getEvisortFolders: build.query({
      queryFn: createQueryFn(getInternalFolders),
    }),
    createS3TokenId: build.mutation({
      queryFn: createQueryFn(createS3TokenId),
    }),
    updateSyncPair: build.mutation({
      queryFn: createQueryFn(updateSyncPair),
    }),
    updateSyncPairTokens: build.mutation({
      queryFn: createQueryFn(upddateTokensSyncPair),
    }),
    createAribaTokenId: build.mutation({
      queryFn: createQueryFn(authenticateAriba),
    }),
    createAribaMultiTokens: build.mutation({
      queryFn: createQueryFn(authenticateMultiTokensAriba),
    }),
    getFileLevelEvents: build.query({
      queryFn: createQueryFn(getFileLevelEvents),
    }),
    getSyncPairs: build.query({
      queryFn: createQueryFn(getSyncPairs),
    }),
    createSyncPair: build.mutation({
      queryFn: createQueryFn(createSyncPair),
    }),
    getSyncPairsAriba: build.query({
      queryFn: createQueryFn(getSyncPairsAriba),
    }),
    getAribaLocations: build.query({
      queryFn: createQueryFn(getAribaLocations),
    }),
    getAribaWorkspaceCount: build.query({
      queryFn: createQueryFn(getAribaWorkspaceCount),
    }),
    getAribaFields: build.query({
      queryFn: createQueryFn(getAribaFields),
    }),
    validateAribaDocumentIdFile: build.mutation({
      queryFn: createQueryFn(validateAribaDocumentIdFile),
    }),
    validateFieldMappingCsv: build.mutation({
      queryFn: createQueryFn(validateFieldMappingCsv),
    }),
    validateSyncPairFolders: build.mutation({
      queryFn: createQueryFn(validateSyncPairFolders),
    }),
    processFieldMappingCsv: build.mutation({
      queryFn: createQueryFn(processFieldMappingCsv),
    }),
    validateRealm: build.mutation({
      queryFn: createQueryFn(validateRealm),
    }),
    updateAribaFileSyncPair: build.mutation({
      queryFn: createQueryFn(updateAribaFileSyncPair),
    }),
    getSyncPairsAribaFailures: build.query({
      queryFn: createQueryFn(getSyncPairsAribaFailures),
    }),
    getCrmConnections: build.query({
      queryFn: createQueryFn(getCrmConnections),
      providesTags: () => [
        {
          type: TagType.CrmConnections,
          id: 'LIST',
        },
      ],
    }),
    deleteCRMIntegration: build.mutation({
      queryFn: createQueryFn(deleteCrmIntegration),
      invalidatesTags: () => [
        {
          type: TagType.CrmConnections,
          id: 'LIST',
        },
      ],
    }),

    //Alerts
    getAlerts: build.query({
      queryFn: createQueryFn(getAlerts),
      providesTags: () => [
        {
          type: TagType.Alerts,
          id: 'LIST',
        },
      ],
    }),
    getAlert: build.query({
      queryFn: createQueryFn(getAlert),
      providesTags: () => [
        {
          type: TagType.Alerts,
          id: 'LIST',
        },
      ],
    }),

    createAlert: build.mutation({
      queryFn: createQueryFn(withOptions(createAlert)),
      invalidatesTags: () => [
        {
          type: TagType.Alerts,
          id: 'LIST',
        },
      ],
    }),
    updateAlert: build.mutation({
      queryFn: createQueryFn(withOptions(updateAlert)),
      invalidatesTags: () => [
        {
          type: TagType.Alerts,
          id: 'LIST',
        },
      ],
    }),
    deleteAlerts: build.mutation({
      queryFn: createQueryFn(withOptions(deleteAlerts)),
      invalidatesTags: () => [
        {
          type: TagType.Alerts,
          id: 'LIST',
        },
      ],
    }),
    toggleMuteAlert: build.mutation({
      queryFn: createQueryFn(withOptions(toggleMuteAlert)),
      invalidatesTags: () => [
        {
          type: TagType.Alerts,
          id: 'LIST',
        },
      ],
    }),
    sendAlertEmail: build.mutation({
      queryFn: createQueryFn(withOptions(sendAlertEmail)),
    }),

    // search
    searchDocuments: build.query({
      queryFn: createQueryFn(searchDocuments),
    }),
    exportExcel: build.query({
      queryFn: createQueryFn(exportSearchResults),
    }),
    validateBooleanQuery: build.query({
      queryFn: createQueryFn(validateBooleanQuery),
    }),
    getFilterSections: build.query({
      queryFn: createQueryFn(getFilterSections),
    }),
    getClauseSuggestions: build.query({
      queryFn: createQueryFn(getClauseSuggestions),
    }),
    getFolderTree: build.query({
      queryFn: createQueryFn(getFolderTree),
      providesTags: () => [
        { type: TagType.Folder, id: 'TREE' },
        { type: TagType.SmartClause, id: 'LIST' },
      ],
    }),
    getFolders: build.query({
      queryFn: createQueryFn(getFolders),
    }),
    saveSearch: build.mutation({
      queryFn: createQueryFn(saveSearch),
    }),
    getSavedSearches: build.query({
      queryFn: createQueryFn(getSavedSearches),
    }),
    deleteSavedSearch: build.mutation({
      queryFn: createQueryFn(deleteSavedSearch),
    }),
    updateSavedSearch: build.mutation({
      queryFn: createQueryFn(updateSavedSearch),
    }),
    checkDownloadExportReportById: build.query({
      queryFn: createQueryFn(checkDownloadExportReportById),
    }),
    getQuickSearchRecords: build.query({
      queryFn: createQueryFn(getQuickSearchRecords),
    }),
    updatePinnedField: build.mutation({
      queryFn: createQueryFn(updatePinnedField),
    }),

    // table views
    getCurrentTableView: build.query({
      queryFn: createQueryFn(getCurrentTableView),
    }),
    getTableViews: build.query({
      queryFn: createQueryFn(getTableViews),
    }),
    createTableView: build.mutation({
      queryFn: createQueryFn(createTableView),
    }),
    updateTableView: build.mutation({
      queryFn: createQueryFn(updateTableView),
    }),
    loadTableView: build.mutation({
      queryFn: createQueryFn(loadTableView),
    }),
    loadDefaultTableView: build.mutation({
      queryFn: createQueryFn(loadDefaultTableView),
    }),
    getDefaultTableView: build.query({
      queryFn: createQueryFn(getDefaultTableView),
    }),
    renameTableView: build.mutation({
      queryFn: createQueryFn(renameTableView),
    }),
    deleteTableView: build.mutation({
      queryFn: createQueryFn(deleteTableView),
    }),

    // documentMetadata
    getDocumentMetadataImportJob: build.query({
      queryFn: createQueryFn(getDocumentMetadataImportJobsData),
    }),

    // document clauses (provisions API)
    getMigratedClauses: build.query({
      queryFn: createQueryFn(getMigratedClauses),
    }),
    getAllClauses: build.query({
      queryFn: createQueryFn(getAllClauses),
    }),
    getAllClausesGrouped: build.query({
      queryFn: createQueryFn(getAllClausesGrouped),
    }),
    updateClauseContent: build.mutation({
      queryFn: createQueryFn(updateClauseContent),
    }),
    deleteDocumentClause: build.mutation({
      queryFn: createQueryFn(deleteDocumentClause),
    }),

    //highlights
    getHighlights: build.query({
      queryFn: createQueryFn(getHighlights),
    }),
    createHighlight: build.mutation({
      queryFn: createQueryFn(postHighlight),
    }),

    // permission
    getEntityPermissions: build.query({
      queryFn: createQueryFn(acl.getEntityPermissions),
    }),

    // access control (Manage Access modal)
    getResourceAccess: build.query({
      queryFn: createQueryFn(getResourceAccess),
      providesTags: (result, error, arg) => [
        {
          type: TagType.AccessControl,
          id: `${arg.resourceType}:${arg.resourceId}`,
        },
      ],
    }),
    updateResourceAccess: build.mutation({
      queryFn: createQueryFn(withOptions(updateResourceAccess)),
      invalidatesTags: (result, error, arg) => [
        {
          type: TagType.AccessControl,
          id: `${arg.resourceType}:${arg.resourceId}`,
        },
      ],
    }),
    getBulkAccessPreview: build.query({
      queryFn: createQueryFn(getBulkAccessPreview),
      keepUnusedDataFor: 0, // preview must reflect current server state, not a stale cache
    }),
    bulkUpdateAccess: build.mutation<
      Awaited<ReturnType<typeof bulkUpdateAccess>>,
      PayloadWithQueryOptions<Parameters<typeof bulkUpdateAccess>[0]>
    >({
      queryFn: createQueryFn(withOptions(bulkUpdateAccess)),
      invalidatesTags: () => [{ type: TagType.AccessControl }],
    }),

    // navigation
    getSearchPositionByHandler: build.query({
      queryFn: createQueryFn(getSearchPositionByHandler),
    }),
    getSearchHandlersByPosition: build.query({
      queryFn: createQueryFn(getSearchHandlersByPosition),
    }),

    //Departments
    getDepartmentChildren: build.query({
      queryFn: createQueryFn(getDepartmentChildren),
    }),

    // ticket document version
    downloadTicketDocumentVersionEmailAttachment: build.query({
      queryFn: createQueryFn(downloadTicketDocumentVersionEmailAttachment),
    }),
    getTicketDocumentVersionEditorKey: build.query({
      queryFn: createQueryFn(getTicketDocumentVersionEditorKey),
    }),

    // User Preferences
    getPinnedFilters: build.query({
      queryFn: createQueryFn(getPinnedFilters),
    }),
    updatePinnedFilters: build.mutation({
      queryFn: createQueryFn(updatePinnedFilters),
    }),

    getRecentSearches: build.query({
      queryFn: createQueryFn(getRecentSearches),
    }),
    updateRecentSearches: build.mutation({
      queryFn: createQueryFn(updateRecentSearches),
    }),

    getHomeSettings: build.query({
      queryFn: createQueryFn(getHomeSettings),
      providesTags: () => [
        { type: TagType.UserPreferences, id: 'homeSettings' },
      ],
    }),
    updateHomeSettings: build.mutation({
      queryFn: createQueryFn(withOptions(updateHomeSettings)),
      invalidatesTags: () => [
        { type: TagType.UserPreferences, id: 'homeSettings' },
      ],
    }),
    applyHomeScope: build.mutation({
      queryFn: createQueryFn(withOptions(applyHomeScope)),
      invalidatesTags: () => [
        { type: TagType.SaveSearch, id: 'LIST' },
        { type: TagType.SaveSearch, id: 'ID' },
        { type: TagType.UserPreferences, id: 'homeSettings' },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (!data) return;
          dispatch(
            api.util.updateQueryData('getHomeSettings', undefined, (draft) => {
              if (draft) draft.data = data.settings;
            }),
          );
        } catch {
          return;
        }
      },
    }),
    clearHomeScope: build.mutation({
      queryFn: createQueryFn(withOptions(clearHomeScope)),
      invalidatesTags: () => [
        { type: TagType.UserPreferences, id: 'homeSettings' },
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (!data) return;
          dispatch(
            api.util.updateQueryData('getHomeSettings', undefined, (draft) => {
              if (draft) draft.data = data.settings;
            }),
          );
        } catch {
          return;
        }
      },
    }),

    // Ticket Reviewer
    getDocumentDetailClauses: build.query({
      queryFn: createQueryFn(getDocumentDetailClauses),
    }),

    //Document
    getDocument: build.query({
      queryFn: createQueryFn(getDocument),
    }),
    getDocumentHandler: build.query({
      queryFn: createQueryFn(getDocumentHandler),
    }),
    getDocumentHandlerPaths: build.query({
      queryFn: createQueryFn(getDocumentHandlerPaths),
    }),

    //documents
    getPdfDocumentOcr: build.query({
      queryFn: createQueryFn(downloadOcrPdf),
    }),
    convertPdfToDocx: build.mutation({
      queryFn: createQueryFn(convertPdfToDocx),
    }),
    getDocumentByTag: build.query({
      queryFn: createQueryFn(getDocumentByTag),
    }),
    getDocumentVersionOcrHtml: build.query({
      queryFn: createQueryFn(getDocumentVersionOcrHtml),
    }),
    getDocumentVersionFileByTag: build.query({
      queryFn: createQueryFn(getDocumentVersionFileByTag),
    }),
    getDocumentFromVersion: build.query({
      queryFn: createQueryFn(getDocumentFromVersion),
      keepUnusedDataFor: 600,
    }),
    getDocumentOriginal: build.query({
      queryFn: createQueryFn(getDocumentOriginal),
    }),
    renameDocument: build.mutation({
      queryFn: createQueryFn(renameDocumentHandler),
    }),
    deleteDocument: build.mutation({
      queryFn: createQueryFn(deleteDocumentHandler),
    }),
    convertContentToSfdt: build.mutation({
      queryFn: createQueryFn(convertContentToSfdt),
    }),
    getDocumentInformation: build.query<
      Awaited<ReturnType<typeof getDocumentFields>>,
      Parameters<typeof getDocumentFields>[0]
    >({
      queryFn: createQueryFn(getDocumentFields),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.documentId})`,
      providesTags: (_result, _error, arg) => [
        {
          type: TagType.Document,
          id: arg.documentId,
        },
      ],
    }),
    getCLMeDocumentInformation: build.query({
      queryFn: createQueryFn(getCLMeDocumentInformation),
      providesTags: (_result, _error, arg) => [
        { type: TagType.CLMeFields, id: arg.tag },
      ],
    }),
    getCLMeClauses: build.query({
      queryFn: createQueryFn(getCLMeDocumentClauses),
      providesTags: (_result, _error, arg) => [
        { type: TagType.CLMeClauses, id: arg.tag },
      ],
    }),
    getCLMeDocumentContent: build.query({
      queryFn: createQueryFn(getCLMeDocumentContent),
    }),
    subscribeToDocumentExtractions: build.query<
      null,
      { pilotDocumentId: string; documentTag?: string }
    >({
      queryFn: () => ({ data: null }),
    }),
    getDocumentSfdt: build.query({
      queryFn: createQueryFn(getDocumentSfdt),
    }),
    compareDocumentsSfdt: build.query({
      queryFn: createQueryFn(compareDocumentsSfdt),
    }),
    saveDocumentSfdt: build.mutation({
      queryFn: createQueryFn(saveDocumentSfdt),
    }),
    backupDocumentSfdt: build.mutation({
      queryFn: createQueryFn(backupDocumentSfdt),
    }),
    updateDocument: build.mutation<
      Awaited<ReturnType<typeof putDocumentUpdate>>,
      Parameters<typeof putDocumentUpdate>[0]
    >({
      queryFn: createQueryFn(putDocumentUpdate),
      invalidatesTags: (_result, _error, arg) => [
        {
          type: TagType.Document,
          id: arg.documentId,
        },
      ],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        // After a successful field update, drop cached Evisearch value
        // suggestions for the affected UF fields so a newly saved/cleared value
        // is reflected on the next fetch. Invalidating a field with no cached
        // suggestions is a no-op, so there's no need to check which fields are
        // searchable. Cross-API invalidation, so it's dispatched here rather than
        // via invalidatesTags.
        try {
          await queryFulfilled;
          const fieldIds = (arg.fields ?? [])
            .map((field) =>
              'uf_field_id' in field ? field.uf_field_id : undefined,
            )
            .filter((id): id is string => Boolean(id));
          if (fieldIds.length > 0) {
            dispatch(
              evisearch.util.invalidateTags(
                fieldIds.map((id) => ({
                  type: EvisearchTags.FilterSuggestion,
                  id,
                })),
              ),
            );
          }
        } catch {
          // Update failed; nothing to invalidate.
        }
      },
    }),
    uploadAttachments: build.mutation({
      queryFn: createQueryFn(uploadAttachments),
    }),
    downloadAttachment: build.query({
      queryFn: createQueryFn(downloadAttachment),
    }),
    downloadDocumentAttachment: build.query({
      queryFn: createQueryFn(downloadDocumentAttachment),
    }),
    tokenizeSfdtContent: build.mutation({
      queryFn: createQueryFn(tokenizeSfdtContent),
    }),
    tokenizeRiskContent: build.query({
      queryFn: createQueryFn(tokenizeRiskContent),
    }),
    getDocumentContent: build.query({
      queryFn: createQueryFn(getDocumentContent),
    }),
    getDocumentAlgoStatus: build.query({
      queryFn: createQueryFn(getDocumentAlgoStatus),
    }),
    getWorkflowFile: build.query({
      queryFn: createQueryFn(getWorkflowFile),
    }),
    searchV2BulkDelete: build.mutation({
      queryFn: createQueryFn(searchV2BulkDelete),
    }),
    searchV2BulkCopy: build.mutation({
      queryFn: createQueryFn(searchV2BulkCopy),
    }),
    searchV2BulkMove: build.mutation({
      queryFn: createQueryFn(searchV2BulkMove),
    }),
    searchV2BulkEditClause: build.mutation({
      queryFn: createQueryFn(searchV2BulkEditClause),
    }),
    searchV2BulkEditData: build.query({
      queryFn: createQueryFn(searchV2BulkEditData),
    }),
    searchV2BulkAddReplaceFields: build.mutation({
      queryFn: createQueryFn(documentBulkAddReplaceFields),
    }),
    searchV2BulkEditFields: build.mutation({
      queryFn: createQueryFn(documentBulkEditFields),
    }),
    //dashboardV2
    createDefaultDashboards: build.query({
      queryFn: createQueryFn(createDefaultDashboards),
    }),
    getDashboardV2: build.query({
      queryFn: createQueryFn(getDashboardV2),
    }),
    getDashboardsV2: build.query({
      queryFn: createQueryFn(getDashboardsV2),
    }),
    saveDashboardChanges: build.mutation({
      queryFn: createQueryFn(saveDashboardChanges),
    }),
    saveAsDashboard: build.mutation({
      queryFn: createQueryFn(saveAsDashboard),
    }),
    updateDashboard: build.mutation({
      queryFn: createQueryFn(updateDashboard),
    }),
    deleteDashboard: build.mutation({
      queryFn: createQueryFn(deleteDashboard),
    }),

    // fields
    createField: build.mutation({
      queryFn: createQueryFn(createField),
    }),
    getFields: build.query({
      queryFn: createQueryFn(getFields),
    }),
    getFieldUfId: build.query({
      queryFn: createQueryFn(getFieldUfId),
    }),
    getDocumentInformationFieldByUfField: build.query({
      queryFn: createQueryFn(getDocumentInformationFieldByUfField),
    }),
    getFieldSections: build.query({
      queryFn: createQueryFn(getFieldSections),
    }),

    //search v3
    getChartData: build.query({
      queryFn: createQueryFn(getChartData),
    }),
    getFilters: build.query({
      queryFn: createQueryFn(getFilters),
    }),
    getFilterSuggestions: build.query({
      queryFn: createQueryFn(getFilterSuggestions),
    }),
    searchV3Documents: build.query({
      queryFn: createQueryFn(searchV3Documents),
    }),
    getSearchExportOptions: build.query({
      queryFn: createQueryFn(getSearchExportOptions),
    }),
    exportExcelV2: build.mutation({
      queryFn: createQueryFn(exportExcelV2),
    }),
    getSearchV3SavedSearches: build.query({
      queryFn: createQueryFn(getSearchV3SavedSearches),
    }),
    deleteSearchV3SavedSearch: build.mutation({
      queryFn: createQueryFn(deleteSearchV3SavedSearch),
    }),
    postSavedSearch: build.mutation({
      queryFn: createQueryFn(postSavedSearch),
    }),
    getSavedSearch: build.query({
      queryFn: createQueryFn(getSavedSearch),
    }),
    putSavedSearch: build.mutation({
      queryFn: createQueryFn(putSavedSearch),
    }),

    //roles
    getRoles: build.query({
      queryFn: createQueryFn(getRoles),
    }),
    getRolesSimple: build.query({
      queryFn: createQueryFn(getRolesSimple),
    }),
    createRole: build.mutation({
      queryFn: createQueryFn(createRole),
    }),
    updateRole: build.mutation({
      queryFn: createQueryFn(updateRole),
    }),
    validateRoleName: build.query({
      queryFn: createQueryFn(validateRoleName),
    }),
    deleteRole: build.mutation({
      queryFn: createQueryFn(deleteRole),
    }),
    getPermissions: build.query({
      queryFn: createQueryFn(getPermissions),
    }),
    getUsers: build.query({
      queryFn: createQueryFn(getUsers),
    }),
    createUser: build.mutation({
      queryFn: createQueryFn(createUser),
    }),
    sendInvitationEmail: build.mutation({
      queryFn: createQueryFn(sendInvitationEmail),
    }),
    updateUser: build.mutation({
      queryFn: createQueryFn(updateUser),
    }),
    deactivateUser: build.mutation({
      queryFn: createQueryFn(deactivateUser),
    }),
    reactivateUser: build.mutation({
      queryFn: createQueryFn(reactivateUser),
    }),
    cancelUserInvite: build.mutation({
      queryFn: createQueryFn(cancelUserInvite),
    }),
    resendReactivatedUserInvite: build.mutation({
      queryFn: createQueryFn(resendReactivatedUserInvite),
    }),
    // Every permission read in the app selects out of this one entry. Held for
    // the whole session by `useInitializeUserPermissions`, so it is only
    // refetched on a tenant switch (the arg) or an explicit `resetApiState`.
    // The long retention is the backstop for the window where that subscription
    // is briefly gone — a tenant switch, or a reset — so permissions survive it
    // instead of costing a fresh round trip on the next read.
    checkAllPermissions: build.query({
      queryFn: createQueryFn(checkAllPermissions),
      keepUnusedDataFor: ONE_HOUR,
    }),

    //ticket
    cancelTicket: build.mutation({
      queryFn: createQueryFn(cancelTicket),
    }),
    completeTicket: build.mutation({
      queryFn: createQueryFn(completeTicket),
    }),
    getTicketDocument: build.query({
      queryFn: createQueryFn(getTicketDocument),
    }),
    getTicketCurrentStage: build.query({
      queryFn: createQueryFn(getTicketCurrentStage),
    }),
    getTicketIntakeForm: build.query({
      queryFn: createQueryFn(getTicketIntakeForm),
    }),
    getTicketSummary: build.query({
      queryFn: createQueryFn(getTicketSummary),
    }),
    getActivities: build.query({
      queryFn: createQueryFn(getActivities),
    }),
    createActivityLogEvent: build.mutation({
      queryFn: createQueryFn(createActivityLog),
    }),
    updateRecipients: build.mutation({
      queryFn: createQueryFn(updateRecipients),
    }),
    deleteRecipients: build.mutation({
      queryFn: createQueryFn(deleteRecipients),
    }),
    bumpVersion: build.mutation({
      queryFn: createQueryFn(bumpVersion),
    }),
    addTicketParticipants: build.mutation({
      queryFn: createQueryFn(addTicketParticipants),
    }),
    getTicketParticipants: build.query({
      queryFn: createQueryFn(getTicketParticipants),
    }),
    collectSignatures: build.query({
      queryFn: createQueryFn(collectSignatures),
    }),
    updateEnvelope: build.mutation({
      queryFn: createQueryFn(updateEnvelope),
    }),
    reassignApproval: build.mutation({
      queryFn: createQueryFn(reassignApproval),
    }),
    getTicketWellKnownFields: build.query({
      queryFn: createQueryFn(getTicketWellKnownFields),
    }),
    postTicketNextStage: build.mutation({
      queryFn: createQueryFn(postTicketNextStage),
    }),
    putTicket: build.mutation({
      queryFn: createQueryFn(putTicket),
    }),
    getSupplierCompany: build.query({
      queryFn: createQueryFn(getSupplierCompany),
    }),
    postTicketSupplierNegotiation: build.mutation({
      queryFn: createQueryFn(postTicketSupplierNegotiation),
    }),
    // Esignature
    assignSigner: build.mutation({
      queryFn: createQueryFn(assignSigner),
    }),
    createEnvelope: build.query({
      queryFn: createQueryFn(createEnvelope),
    }),
    getEnvelope: build.query({
      queryFn: createQueryFn(getEnvelope),
    }),
    checkEnvelope: build.query({
      queryFn: createQueryFn(checkEnvelope),
    }),
    getRecipients: build.query({
      queryFn: createQueryFn(getRecipients),
    }),
    sendReminder: build.mutation({
      queryFn: createQueryFn(sendReminder),
    }),
    viewEnvelope: build.query({
      queryFn: createQueryFn(viewEnvelope),
    }),
    signDocument: build.query({
      queryFn: createQueryFn(signDocument),
    }),
    getEsignatureActivities: build.query({
      queryFn: createQueryFn(getEsignatureActivities),
    }),
    validateMultiTicketBundle: build.mutation({
      queryFn: createQueryFn(withOptions(validateMultiTicketBundle)),
    }),
    createMultiTicketBundle: build.mutation({
      queryFn: createQueryFn(withOptions(createMultiTicketBundle)),
    }),

    // Turn Tracking
    getTurnTrackingHistory: build.query({
      queryFn: createQueryFn(getTurnTrackingHistory),
    }),
    getTurnTrackingParties: build.query({
      queryFn: createQueryFn(getTurnTrackingParties),
    }),
    updateTurnTrackingEntry: build.mutation({
      queryFn: createQueryFn(updateTurnTrackingEntry),
    }),
    createTurnTrackingEntry: build.mutation({
      queryFn: createQueryFn(createTurnTrackingEntry),
    }),

    // Conversational AI
    acceptLegalDisclaimer: build.query({
      queryFn: createQueryFn(acceptLegalDisclaimer),
    }),
    getConversations: build.query({
      queryFn: createQueryFn(getConversations),
      keepUnusedDataFor: 20,
    }),
    getConversationHistory: build.query({
      queryFn: createQueryFn(getConversationHistory),
      keepUnusedDataFor: 20,
    }),
    createConversation: build.mutation({
      queryFn: createQueryFn(createConversation),
    }),
    getConversation: build.query({
      queryFn: createQueryFn(getConversation),
    }),
    postUserMessage: build.mutation({
      queryFn: createQueryFn(postUserMessage),
    }),
    postUserFeedback: build.mutation({
      queryFn: createQueryFn(postUserFeedback),
    }),
    interruptAiMessage: build.mutation({
      queryFn: createQueryFn(interruptAiMessage),
    }),
    patchConversation: build.mutation({
      queryFn: createQueryFn(patchConversation),
    }),
    deleteConversation: build.mutation({
      queryFn: createQueryFn(deleteConversation),
    }),

    // Conversational AI V2
    createAgentExecution: build.mutation({
      queryFn: createQueryFn(createAgentExecution),
    }),
    getAgentExecution: build.query({
      queryFn: createQueryFn(getAgentExecution),
    }),
    getAgentExecutions: build.query({
      queryFn: createQueryFn(getAgentExecutions),
    }),
    sendAgentExecutionMessage: build.mutation({
      queryFn: createQueryFn(sendAgentExecutionMessage),
    }),
    interruptAgentExecution: build.mutation({
      queryFn: createQueryFn(interruptAgentExecution),
    }),
    patchAgentExecutionMessage: build.mutation({
      queryFn: createQueryFn(patchAgentExecutionMessage),
    }),
    streamAgentExecution: build.query({
      queryFn: (_arg: StreamAgentExecutionArgs) => ({
        data: INITIAL_STREAM_STATE,
      }),
      keepUnusedDataFor: 0,
      onCacheEntryAdded: onStreamCacheEntryAdded,
    }),

    getExternalDocumentForDebug: build.query({
      queryFn: createQueryFn(getExternalDocumentForDebug),
    }),

    // Gen AI
    getGenClauseResult: build.query({
      queryFn: createQueryFn(getGenClauseResult),
    }),
    getGenClauseResults: build.query({
      queryFn: createQueryFn(getGenClauseResults),
    }),

    // resolvers
    resolveDocument: build.query({
      queryFn: createQueryFn(resolveDocument),
      keepUnusedDataFor: 600,
    }),
    resolveUsers: build.query({
      queryFn: createQueryFn(resolveUsers),
    }),
    resolveUser: build.query({
      queryFn: createQueryFn(resolveUser),
    }),
    resolveCitation: build.query({
      queryFn: createQueryFn(resolveCitation),
      keepUnusedDataFor: 30,
    }),

    //post sig document
    getDocumentsViewsetFields: build.query({
      queryFn: createQueryFn(getDocumentsViewsetFields),
    }),
    getFolderData: build.query({
      queryFn: createQueryFn(getFolderData),
      providesTags: () => [{ type: TagType.Folder, id: 'CONTENT' }],
    }),
    getGroupForDocument: build.query({
      queryFn: createQueryFn(fetchGroupForDocument),
    }),

    // upload
    getUploadFiles: build.query({
      queryFn: createQueryFn(getUploadFiles),
      providesTags: () => [
        {
          type: TagType.UploadFiles,
          id: 'LIST',
        },
      ],
    }),
    addUploadFiles: build.mutation({
      queryFn: createQueryFn(addUploadFiles),
      invalidatesTags: () => [
        {
          type: TagType.UploadFiles,
          id: 'LIST',
        },
        {
          type: TagType.Folder,
          id: 'CONTENT',
        },
      ],
    }),

    // folder
    getFolderVisibility: build.query({
      queryFn: createQueryFn(getFolderVisibility),
    }),

    getFolderAccessList: build.query({
      queryFn: createQueryFn(getFolderAccessList),
      providesTags: () => [
        {
          type: TagType.Folder,
          id: 'LIST',
        },
      ],
    }),

    updateFolderAccessSettings: build.mutation({
      queryFn: createQueryFn(updateFolderAccessSettings),
      invalidatesTags: () => [
        {
          type: TagType.Folder,
          id: 'LIST',
        },
      ],
    }),
    folderBulkMove: build.mutation({
      queryFn: createQueryFn(folderBulkMove),
      invalidatesTags: (_result, error, arg) => {
        if (error || (!arg.selectAll && arg.folders.length === 0)) {
          return [];
        }

        return [{ type: TagType.Folder, id: 'TREE' }];
      },
    }),
    folderBulkCopy: build.mutation({
      queryFn: createQueryFn(folderBulkCopy),
      invalidatesTags: (_result, error, arg) => {
        if (error || (!arg.selectAll && arg.folders.length === 0)) {
          return [];
        }

        return [{ type: TagType.Folder, id: 'TREE' }];
      },
    }),
    createFolder: build.mutation({
      queryFn: createQueryFn(createFolder),
      invalidatesTags: (_result, error) => {
        if (error) {
          return [];
        }

        return [{ type: TagType.Folder, id: 'TREE' }];
      },
    }),
    renameFolder: build.mutation({
      queryFn: createQueryFn(renameFolder),
      invalidatesTags: (_result, error) => {
        if (error) {
          return [];
        }

        return [{ type: TagType.Folder, id: 'TREE' }];
      },
    }),

    // Keep this mutation side-effect free at the tag layer. The documents page
    // explicitly refreshes folder tree via getFolderTree query and syncs the legacy
    // client slice from that single response to avoid duplicate backend calls.
    folderBulkDelete: build.mutation({
      queryFn: createQueryFn(folderBulkDelete),
    }),

    // process documents
    getAlgorithmMetrics: build.query({
      queryFn: createQueryFn(getAlgorithmMetrics),
    }),
    reprocessAlgorithms: build.mutation({
      queryFn: createQueryFn(reprocessAlgorithms),
    }),
    stopReprocessAlgorithms: build.mutation({
      queryFn: createQueryFn(stopReprocessAlgorithms),
    }),

    // Manage Party Lists
    getParties: build.query({
      queryFn: createQueryFn(getParties),
    }),
    updateParty: build.mutation({
      queryFn: createQueryFn(updateParty),
    }),
    deleteParty: build.mutation({
      queryFn: createQueryFn(deleteParty),
    }),
    addParty: build.mutation({
      queryFn: createQueryFn(addParty),
    }),
    uploadParty: build.mutation({
      queryFn: createQueryFn(uploadParty),
    }),
    deleteParties: build.mutation({
      queryFn: createQueryFn(deleteParties),
    }),
    // Super Admin
    createSuperAdmin: build.mutation({
      queryFn: createQueryFn(createSuperAdmin),
    }),
    deactivateSuperAdmin: build.mutation({
      queryFn: createQueryFn(deactivateSuperAdmin),
    }),

    // Wopi
    getWopiSetup: build.query({
      queryFn: createQueryFn(getWopiSetup),
    }),
    getWopiStaticFile: build.query({
      queryFn: createQueryFn(getWopiStaticFiles),
    }),

    // CodeCash
    createOneTimeCreditBalance: build.mutation({
      queryFn: createQueryFn(createOneTimeCreditBalance),
    }),
    editOneTimeCreditBalance: build.mutation({
      queryFn: createQueryFn(editOneTimeCreditBalance),
    }),
    getCreditSummary: build.query({
      queryFn: createQueryFn(getCreditSummary),
    }),
    getCreditEstimation: build.query({
      queryFn: createQueryFn(getCreditEstimation),
    }),
    getCreditLedger: build.query({
      queryFn: createQueryFn(getCreditLedger),
    }),
    updateRecurringCredits: build.mutation({
      queryFn: createQueryFn(updateRecurringCredits),
    }),

    // Audit Logs
    getAuditLogs: build.query({
      queryFn: createQueryFn(getAuditLogs),
    }),

    // External
    getExternalDocument: build.query({
      queryFn: createQueryFn(getExternalDocument),
    }),
    // Webhooks
    getEndpoints: build.query({
      queryFn: createQueryFn(getEndpoints),
      providesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),
    createEndpoint: build.mutation({
      queryFn: createQueryFn(createEndpoint),
      invalidatesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),
    deleteEndpoint: build.mutation({
      queryFn: createQueryFn(deleteEndpoint),
      invalidatesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),
    suspendEndpoint: build.mutation({
      queryFn: createQueryFn(suspendEndpoint),
      invalidatesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),
    activateEndpoint: build.mutation({
      queryFn: createQueryFn(activateEndpoint),
      invalidatesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),
    updateEndpoint: build.mutation({
      queryFn: createQueryFn(updateEndpoint),
      invalidatesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),

    rotateEndpointKey: build.mutation({
      queryFn: createQueryFn(rotateEndpointKey),
      invalidatesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),
    getConfigurationEvents: build.query({
      queryFn: createQueryFn(getConfigurationEvents),
    }),

    getEventSamplePayload: build.query({
      queryFn: createQueryFn(getEventSamplePayload),
    }),

    getEventFields: build.query({
      queryFn: createQueryFn(getEventFields),
    }),

    getAttempts: build.query({
      queryFn: createQueryFn(getAttempts),
      providesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),
    getAttemptsExport: build.query({
      queryFn: createQueryFn(getAttemptsExport),
    }),
    archiveAttempt: build.mutation({
      queryFn: createQueryFn(archiveAttempt),
      invalidatesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),
    resendAttempt: build.mutation({
      queryFn: createQueryFn(resendAttempt),
      invalidatesTags: () => [
        {
          type: TagType.Webhooks,
          id: 'LIST',
        },
      ],
    }),

    // WSS
    resolveWssTicketId: build.query({
      queryFn: createQueryFn(resolveWssTicketId),
    }),
    resolveWssDocument: build.query({
      queryFn: createQueryFn(resolveWssDocument),
    }),
    getWorkflowContractTypes: build.query({
      queryFn: createQueryFn(getWorkflowContractTypes),
    }),

    // Workday
    getWorkdayUpcApps: build.query({
      queryFn: createQueryFn(getWorkdayUpcApps),
      keepUnusedDataFor: 600,
    }),

    // settings
    updateCurrentUser: build.mutation({
      queryFn: createQueryFn(updateCurrentUser),
    }),

    updatePassword: build.mutation({
      queryFn: createQueryFn(updatePassword),
    }),

    // Microsoft Sharepoint Embedded integration
    getSharepointEmbeddedUrl: build.mutation({
      queryFn: createQueryFn(getSharepointEmbeddedUrl),
      invalidatesTags: () => [
        {
          type: TagType.SharepointOpenDocument,
          id: 'LIST',
        },
      ],
    }),
    getEditSessionStatus: build.query({
      queryFn: createQueryFn(getEditSessionStatus),
      providesTags: () => [
        {
          type: TagType.SharepointOpenDocument,
          id: 'LIST',
        },
      ],
    }),
    unlockSharepointDocument: build.mutation<
      void,
      UnlockSharepointDocumentParams
    >({
      queryFn: createQueryFn(unlockSharepointDocument),
      invalidatesTags: () => [
        {
          type: TagType.SharepointOpenDocument,
          id: 'LIST',
        },
      ],
    }),
    createSharepointConfiguration: build.mutation({
      queryFn: createQueryFn(withOptions(createSharepointConfiguration)),
      invalidatesTags: () => [
        {
          type: TagType.SharepointConfiguration,
          id: 'LIST',
        },
      ],
    }),
    getSharepointConfigurationStatus: build.query({
      queryFn: createQueryFn(getSharepointConfigurationStatus),
      providesTags: () => [
        {
          type: TagType.SharepointConfiguration,
          id: 'LIST',
        },
      ],
    }),
    deleteSharepointConfiguration: build.mutation({
      queryFn: createQueryFn(withOptions(deleteSharepointConfiguration)),
      invalidatesTags: () => [
        {
          type: TagType.SharepointConfiguration,
          id: 'LIST',
        },
      ],
    }),
    getGraphApiPermissions: build.query({
      queryFn: createQueryFn(getGraphApiPermissions),
    }),

    // Integrations
    getESignatureIntegrationsV1: build.query({
      queryFn: createQueryFn(getESignatureIntegrationsV1),
    }),

    // Routing
    searchRouting: build.query({
      queryFn: createQueryFn(searchRouting),
    }),
    getRoutingById: build.query({
      queryFn: createQueryFn(getRoutingById),
    }),
    createRouting: build.mutation({
      queryFn: createQueryFn(createRouting),
    }),
    updateRouting: build.mutation({
      queryFn: createQueryFn(updateRouting),
    }),
    deleteRouting: build.mutation({
      queryFn: createQueryFn(deleteRouting),
    }),
    getUnroutedWorkflows: build.query({
      queryFn: createQueryFn(getUnroutedWorkflows),
    }),

    // Routing Configuration
    getRoutingConfiguration: build.query({
      queryFn: createQueryFn(getRoutingConfiguration),
    }),
    patchRoutingConfiguration: build.mutation({
      queryFn: createQueryFn(patchRoutingConfiguration),
    }),

    // Agent Forge
    getAgentForgeInvocations: build.mutation({
      queryFn: createQueryFn(getAgentForgeInvocations),
    }),

    // Currencies
    getCurrencies: build.query({
      queryFn: createQueryFn(getCurrencies),
    }),

    // Localization
    getLocalizationPreferences: build.query({
      queryFn: createQueryFn(getLocalizationPreferences),
    }),
    updateLocalizationPreferences: build.mutation({
      queryFn: createQueryFn(updateLocalizationPreferences),
    }),
  }),
});

export const endpointNames = Object.keys(api.endpoints);
